<template>
  <div class="bg-white p-4">
    <CategoryTop :title="$t('pageName.rewardsOverview')" :description="$t('kickbackInfo.description')"
      :breadcrumbs-parent-routes="['account']" content-class="px-4 mt-4" left-col="10" right-col="2">
      <template v-if="groupImage && isDealer" #right>
        <DashboardCard :border="false" class="d-flex justify-content-end">
          <template v-slot:content>
            <img :src="groupImage" />
          </template>
        </DashboardCard>
      </template>
    </CategoryTop>
    <Loader :show="loading" v-if="loading"></Loader>
    <div v-else>
      <div>
        <MDBRow v-for="group in groupsTables" :key="group.groupId">
          <MDBCol xl="6" cols="12">
            <MDBTable borderless class="simple-table">
              <thead>
                <tr>
                  <th class="th--height">{{ $t('rewardsOverview.groupLevel') }}</th>
                  <th class="th--height" v-html="$t('rewardsOverview.titlePoints')"></th>
                  <th class="th--height">14-16"</th>
                  <th class="th--height">17-18"</th>
                  <th class="th--height">19-22"</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, idx) in group.rows" :key="row.name + '-' + idx">
                  <td v-if="idx === 0" :rowspan="group.rows.length" class="td--width">
                    {{ group.groupName }}
                  </td>
                  <td>{{ row.title }} <small v-if="row.extra_period">(alleen in de actieperiode
                      15-9/15-11-2025)</small></td>
                  <!-- Points -->
                  <td>{{ row.marketing[0] ?? 0 }}</td>
                  <td>{{ row.marketing[1] ?? 0 }}</td>
                  <td>{{ row.marketing[2] ?? 0 }}</td>
                </tr>
              </tbody>
            </MDBTable>

          </MDBCol>
          <MDBCol xl="6" cols="12">
            <MDBTable borderless class="simple-table">
              <thead>
                <tr>
                  <th class="th--height">{{ $t('rewardsOverview.groupLevel') }}</th>
                  <th class="th--height">{{ $t('rewardsOverview.titleKickback') }}</th>
                  <th class="th--height">14-16"</th>
                  <th class="th--height">17-18"</th>
                  <th class="th--height">19-22"</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, idx) in group.rows" :key="row.name + '-' + idx">
                  <td v-if="idx === 0" :rowspan="group.rows.length" class="td--width">
                    {{ group.groupName }}
                  </td>
                  <td>{{ row.title }}</td>
                  <!-- bonus in euro -->
                  <td>{{ row.bonus[0] != null ? row.bonus[0].toFixed(2) + ' €' : '0.00 €' }}</td>
                  <td>{{ row.bonus[1] != null ? row.bonus[1].toFixed(2) + ' €' : '0.00 €' }}</td>
                  <td>{{ row.bonus[2] != null ? row.bonus[2].toFixed(2) + ' €' : '0.00 €' }}</td>
                </tr>
              </tbody>
            </MDBTable>
          </MDBCol>
        </MDBRow>

        <MDBRow>
          <MDBCol xl="6" cols="12">{{ $t("rewardsOverview.footerPoints") }}</MDBCol>
          <MDBCol xl="6" cols="12">{{ $t("rewardsOverview.kickbackPoints") }}</MDBCol>
        </MDBRow>
      </div>
      <MultiplicatorInfo />
    </div>
  </div>
</template>

<script setup>
import { MDBCol, MDBRow, MDBTable } from "mdb-vue-ui-kit";
import CategoryTop from "@/components/General/CategoryTop/index.vue";
import DashboardCard from "@/components/User/Wholesaler/DashboardCard.vue";
import MultiplicatorInfo from "@/components/KickbackFee/MultiplicatorInfo.vue";
import Loader from "@/components/General/Loader/index";
import { useStore } from "vuex";
import { computed, ref, onMounted } from "vue";

const store = useStore();

const groups = computed(() => store.getters.getGroups);
const loading = computed(() => store.getters.getPending);
const user = computed(() => store.getters.getMe);
const meIsPending = computed(() => store.getters.getMeIsPending);
const currentLocale = computed(() => store.getters.getCurrentLocale);
const groupImage = ref(null);
const isDealer = computed(() =>
  Boolean(user.value?.roles?.some((item) => item.name === "dealer"))
);
const groupsTables = ref([]);
const setGroupName = (id) => {
  const output = groups.value.filter((item) => {
    return item.id === id;
  });

  if (!output.length) {
    return "";
  }

  groupImage.value =
    output[0].gka === "FSP" || output[0].gka === "Profile"
      ? require(`@/assets/img/${output[0].gka}_logo.png`)
      : null;

  return output[0].group;
};

function buildGroupTables(raw) {
  const byGroup = new Map();

  for (const [modelName, items] of Object.entries(raw)) {
    for (const item of items) {
      const gId = item.group_id;
      const extra_period = item.extra_period;
      if (!byGroup.has(gId)) {
        byGroup.set(gId, {
          groupId: gId,
          groupName: setGroupName(gId),
          _rowsMap: new Map()
        });
      }
      const groupObj = byGroup.get(gId);

      if (!groupObj._rowsMap.has(modelName)) {
        groupObj._rowsMap.set(modelName, {
          name: modelName,
          title: item.name?.[currentLocale.value] || modelName,
          marketing: [null, null, null],
          bonus: [null, null, null],
          extra_period: extra_period
        });
      }
      const row = groupObj._rowsMap.get(modelName);

      const idx =
        item.inches_from === 14 && item.inches_to === 16 ? 0 :
          item.inches_from === 17 && item.inches_to === 18 ? 1 :
            item.inches_from === 19 && item.inches_to === 22 ? 2 : -1;

      if (idx !== -1) {
        row.marketing[idx] = item.points ?? row.marketing[idx];
        row.bonus[idx] = item.kickback_fee ?? row.bonus[idx];
      }
    }
  }

  const result = [];
  for (const g of byGroup.values()) {
    const rows = Array.from(g._rowsMap.values())
      .sort((a, b) => a.name.localeCompare(b.name));
    result.push({
      groupId: g.groupId,
      groupName: g.groupName,
      rows
    });
  }

  result.sort((a, b) => a.groupName.localeCompare(b.groupName));
  return result;
}

onMounted(async () => {
  if (!meIsPending.value) {
    await store.dispatch("me");
  }
  let groupIds = "0";
  await store.dispatch("loadGroups");
  if (isDealer.value) {
    groupIds = "0";
  } else {
    groupIds =
      groups.value?.map((g) => g.id).filter((id) => id != null).join(",") || "0";
  }
  await store.dispatch("loadKickbackInfo", groupIds);
  const raw = store.getters.getKickbackInfo;
  groupsTables.value = buildGroupTables(
    raw && typeof raw === "object" && !Array.isArray(raw) ? raw : {}
  );

  const currentYear = new Date().getFullYear();
  await store.dispatch("fetchTyreLimit", currentYear);
});
</script>

<style lang="scss" scoped src="../../styles/KickbackFeeContent.scss"></style>
